// src/pages/locations/cities/CreateCityPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  useCountries,
  useRegions,
  useCreateCityMutation,
} from "@/api/features/locations";
import CityForm from "@/components/locations/forms/CityForm";

import LocationFormPage from "../components/LocationFormPage";
import { useLeaveConfirm } from "../components/form/useLeaveConfirm";
import { useLocationScope } from "../useLocationScope";
import { locationCreatePath, locationTabPath } from "../locations.access";
import { withFlash } from "../flash";

const CreateCityPage: React.FC = () => {
  const navigate = useNavigate();
  const { country, region, scope } = useLocationScope();

  const backTo = locationTabPath("cities", scope);

  // Mirrors the country chosen inside the form so the region list matches it.
  const [formCountry, setFormCountry] = React.useState(country);
  const activeCountry = formCountry || country;

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });
  const countries = countriesRes?.items ?? [];

  const { data: regionsRes, isLoading: regionsLoading } = useRegions(
    activeCountry
      ? { countryIso2: activeCountry, page: 1, limit: 100 }
      : { countryIso2: "" }
  );
  const regions = regionsRes?.items ?? [];

  const createMutation = useCreateCityMutation();
  const { requestCancel, dialog } = useLeaveConfirm(() => navigate(backTo));

  const [resetSignal, setResetSignal] = React.useState(0);

  return (
    <LocationFormPage
      entity="cities"
      title="Add city"
      backTo={backTo}
      backLabel="Back to Cities"
    >
      <CityForm
        mode="create"
        countries={countries}
        countriesLoading={countriesLoading}
        regions={regions}
        regionsLoading={Boolean(activeCountry) && regionsLoading}
        defaultCountryIso2={activeCountry}
        // "null" is the list's filter for "no region", not a real region id.
        defaultRegionId={region && region !== "null" ? region : ""}
        onCountryChange={setFormCountry}
        addRegionHref={locationCreatePath("regions", { country: activeCountry })}
        isSubmitting={createMutation.isPending}
        error={createMutation.error}
        resetSignal={resetSignal}
        onCancel={requestCancel}
        onSubmit={(values, { addAnother }) =>
          createMutation.mutate(
            {
              countryIso2: values.countryIso2,
              regionId: values.regionId ? Number(values.regionId) : null,
              name: values.name,
              ...(values.nameNorm ? { nameNorm: values.nameNorm } : {}),
              latitude: values.latitude || null,
              longitude: values.longitude || null,
            },
            {
              onSuccess: (created) => {
                if (addAnother) {
                  setResetSignal((n) => n + 1);
                  return;
                }
                navigate(
                  locationTabPath("cities", {
                    country: created?.countryIso2 ?? values.countryIso2,
                  }),
                  withFlash(`${created.name} was added.`)
                );
              },
            }
          )
        }
      />

      {dialog}
    </LocationFormPage>
  );
};

export default CreateCityPage;
